import graphqlify from 'graphqlify';

import {VarietyCollection} from '../../shared/collection/md/VarietyCollection';
import {TillageSystemCollection} from '../../shared/collection/md/TillageSystemCollection';
import {CountryUtils} from '../../shared/utils/CountryUtils';
import {ResourcesConfig} from './config';
import {router} from '../router';
import {BaseModel} from '../../shared/model/base/BaseModel';
import cookieReader from 'cookies-js';
import {app} from '../app';
import {
    CROPS,
    CURRENT_CROP_SEASONS,
    CURRENT_FARM,
    CURRENT_FARM_FEATURES,
    CURRENT_FIELDS,
    CURRENT_USER,
    STRESSES
} from './SessionObjects';
import {BaseFilterModel} from '../../shared/model/filter/BaseFilterModel';
import {CropUsageCollection} from '../../shared/collection/md2/CropUsageCollection';
import {SoilTextureCollection} from '../../shared/collection/md2/SoilTextureCollection';
import {SoilTaxonomyCollection} from '../../shared/collection/md2/SoilTaxonomyCollection';
import {FarmCollection} from '../../shared/collection/farms/FarmCollection';

/**
 * @class GraphqlConfig
 *
 * @description This configuration is used to generate graphql query  by using graphqlify
 *              and fetch data from graphql service and then parse
 *
 * @example Below is an example how to generate the desired graphql query
 *
 *      {
 *          farm = {
 *              params: {
 *                  uuid: "d2354970-f409-46ba-a2df-3632c4e38ab6"
 *              },
 *              fields: {                                                   {
 *                  uuid: {},                                                   farm(uuid:"d2354970-f409-46ba-a2df-3632c4e38ab6"){
 *                  name: {},                                                       uuid,
 *                  fields: {                                                       name,
 *                      fields: {                                   ===             fields{
 *                          uuid: {},                               ===                 uuid,
 *                          name: {},                               ===                 name,
 *                          area: {},                                                   area,
 *                          cropSeason: {                                               cropSeason: {
 *                              fields: {                                                   uuid,
 *                                  uuid: {},                                               startDate,
 *                                  startDate: {}                                           endDate
 *                                  endDate: {}                                         }
 *                              }                                                   }
 *                          }                                                   }
 *                      }                                                   }
 *                  }
 *              }
 *          }
 *      }
 */
export class GraphqlConfig {

    static user (ev) {

        let user = CURRENT_USER;

        if(ENV === 'demo') {
            user.set({
                uuid: 'creator_id__1',
                email: 'demo_user@pokemail.net',
                isVerified: true
            });

            user.fetchDeps();
            return $.when(user.depsFetched).then(() => user);
        }

        if (ev.queryParams && ev.queryParams.email && user.attributes.email !== ev.queryParams.email) {

            user.set('email', ev.queryParams.email);
        }

        // User already initiated (route change or page reload)
        if (user.get('uuid')) {

            // Dependencies not fetched => get settings and distributors
            if (_.isNull(user.depsFetched)) {
                user.fetchDeps();
            }

            // Profile is not fetched => get profile from gigya
            if (_.isNull(user.profileFetched)) {

                user.fetchProfile({extraProfileFields: 'address,phones,languages', include: 'profile, data, emails', UID: user.get('uuid')}).done((response) => {

                    if(response && response.profile){
                        user.setProfileInfo(response.profile, response.data, response.isVerified, response.emails);
                    }

                    let countryCode = router.getLastEventCountryCode();
                    let localeCode = router.getLastEventLanguageCode();

                    if (user.checkCountryMismatch(countryCode, localeCode)) {
                        return $.Deferred().reject(user);
                    }
                });
            }

            return  $.when(user.depsFetched, user.profileFetched).then(() => user);
        }

        // Login page state.
        return $.Deferred().resolve(user);
    }

    static fetchFarm (ev) {

        let def = $.Deferred();

        let next = ev.routeNamedArgs.farmUuid;
        let curr = CURRENT_FARM.get('uuid');

        if (next && curr !== next) {

            CURRENT_FARM.set('uuid', next);
            CURRENT_FARM.set('permission', undefined);

            if (next === 'dashboard') {

                def.resolve(CURRENT_FARM);
            } else {

                let selectedFarm = new FarmCollection([], {
                    filterParams : new BaseFilterModel({
                        uuid: next
                    })
                });

                selectedFarm
                    .update({headers: {IGNORE_HTTP_ERROR: [403]}})
                    .done(() => {

                        if (selectedFarm.length) {

                            CURRENT_FARM.set(_.head(selectedFarm.models).attributes);

                            if (BaseModel.timezone !== CURRENT_FARM.get('timezone')) {
                                CURRENT_FARM.updateBaseTimezone();
                            }

                            def.resolve(CURRENT_FARM);
                        } else {

                            GraphqlConfig.fetchFarmFail()
                                .always(() => {
                                    def.resolve(CURRENT_FARM);
                                });
                        }
                    })
                    .fail(() => {

                        GraphqlConfig.fetchFarmFail()
                            .always(() => {
                                def.resolve(CURRENT_FARM);
                            });
                    });
            }
        } else {
            def.resolve(CURRENT_FARM);
        }

        return def;
    }

    static fetchFarmFail () {

        let def = $.Deferred();
        let farms = new FarmCollection();

        farms.update()
            .done(() => {

                let filteredFarms = _.filter(farms.models, (farm) => { return farm.get('creatorId') === CURRENT_USER.get('uuid'); });

                if (filteredFarms.length > 0) {

                    CURRENT_USER.get('settings').setSetting(
                        'TAMS',
                        'LAST_FARM_SELECTED',
                        filteredFarms[0].get('uuid')
                    );

                    CURRENT_FARM.set(filteredFarms[0].attributes);

                    if (BaseModel.timezone !== CURRENT_FARM.get('timezone')) {
                        CURRENT_FARM.updateBaseTimezone();
                    }

                    def.resolve(CURRENT_FARM);
                    router.navigateRelativelyToLocaleCode(`/farm/${filteredFarms[0].get('uuid')}/field/status`);
                } else {

                    CURRENT_USER.createFirstFarm()
                        .done((farm) => {

                            CURRENT_USER.get('settings').setSetting(
                                'TAMS',
                                'LAST_FARM_SELECTED',
                                farm.get('uuid')
                            );

                            CURRENT_FARM.set(farm.attributes);

                            if (BaseModel.timezone !== CURRENT_FARM.get('timezone')) {
                                CURRENT_FARM.updateBaseTimezone();
                            }

                            def.resolve(CURRENT_FARM);
                            router.navigateRelativelyToLocaleCode(`/farm/${farm.get('uuid')}/field/status`);
                        });
                }
            })
            .fail(() => {
                def.reject();
            });

        return def;
    }

    static crops (queryObject, languageCode) {

        let userUuid = CURRENT_USER.get('uuid');

        if (userUuid && !CROPS.length) {

            queryObject.crops = {
                params: {languageCode},
                fields: {
                    uuid: {},
                    name: {}
                }
            };
        }
    }

    static stresses (queryObject, languageCode) {

        let userUuid = CURRENT_USER.get('uuid');

        if (userUuid && !STRESSES.length) {

            queryObject.stressesV2 = {
                params: {languageCode},
                fields: {
                    uuid: {},
                    name: {},
                    code: {},
                    scientificName: {},
                    taxonomyUuid: {},
                    taxonomyCode: {},
                    stressTypeUuid: {},
                    stressTypeCode: {}
                }
            };
        }
    }

    static varieties (queryObject, languageCode, countryUuid, registrationStatusUuid) {

        queryObject.varieties = {
            params: {
                languageCode,
                countryUuid: countryUuid,
                registrationStatusUuid: registrationStatusUuid
            },
            fields: {
                uuid: {},
                name: {},
                crop: {
                    fields: {
                        uuid: {}
                    }
                },
                breeder: {}
            }
        };
    }

    static tillageSystems (queryObject, languageCode) {

        queryObject.tillageSystems = {
            params: {languageCode},
            fields: {
                uuid: {},
                name: {}
            }
        };
    }

    static cropUsages (queryObject, languageCode) {

        queryObject.cropUsages = {
            params: {languageCode},
            fields: {
                uuid: {},
                name: {}
            }
        };
    }

    static soilTextures (queryObject, languageCode) {

        queryObject.soilTextures = {
            params: {languageCode},
            fields: {
                uuid: {},
                name: {}
            }
        };
    }

    static soilTaxonomies (queryObject, languageCode) {

        queryObject.soilTaxonomies = {
            params: {languageCode},
            fields: {
                uuid: {},
                name: {}
            }
        };
    }

    static farm (queryObject, ev) {

        let next = ev.routeNamedArgs.farmUuid;
        let curr = CURRENT_FARM.get('uuid');

        if (next && ((curr !== next) ||
            !CURRENT_FARM_FEATURES.length ||
            !CURRENT_FIELDS.length)) {

            if (!_.isNil(next) && !_.isNil(curr) &&
                CURRENT_FARM_FEATURES.length) {

                CURRENT_FARM.clear();
                CURRENT_FARM_FEATURES.reset();
                CURRENT_FIELDS.reset();
                CURRENT_CROP_SEASONS.reset();
            }

            if (next === 'dashboard') {
                return;
            }

            //CURRENT_FARM.set('uuid', next); // Done in static fetchFarmMethod

            queryObject.farm = {
                params: {uuid: next},
                fields: {
                    uuid: {},
                    name: {},
                    creatorUuid: {},
                    access: {},
                    timezone: {},
                    latitude: {},
                    longitude: {},
                }
            };
        }
    }

    static farmFeatures (queryObject, ev) {

        let farmFeatures = CURRENT_FARM_FEATURES;
        let curr = farmFeatures.get('farmUuid');
        let next = ev.routeNamedArgs.farmUuid;

        try {

            if (next && curr !== next) {

                queryObject.farm.fields.features = {
                    fields: {
                        farmUuid: {},
                        crop: {
                            fields: {
                                uuid: {}
                            }
                        },
                        name: {},
                        validTill: {},
                        packageUuid: {},
                        packageType: {}
                    }
                };
            }
        } catch (e) {}
    }

    static fields (queryObject, ev) {

        let next = ev.routeNamedArgs.farmUuid;
        let curr = CURRENT_FIELDS.farmUuid;

        try {

            if ((next && curr !== next) ||
                !CURRENT_CROP_SEASONS.length) {

                CURRENT_FIELDS.farmUuid = next;

                queryObject.farm.fields.fieldsV2 = {
                    fields: {
                        uuid: {},
                        name: {},
                        area: {},
                        boundary: {},
                        heading: {},
                        boundarySvg: {},
                        soilTexture: {
                            fields: {
                                uuid: {}
                            }
                        },
                        soilTaxonomy: {
                            fields: {
                                uuid: {}
                            }
                        },
                        farm: {
                            fields: {
                                uuid: {}
                            }
                        },
                        features: {
                            fields: {
                                fieldUuid: {},
                                features: {},
                                crop: {
                                    fields: {
                                        uuid: {}
                                    }
                                },
                                isSponsoredField: {}
                            }
                        }
                    }
                };
            }
        } catch (e) {}
    }

    static cropSeasons (queryObject) {

        try {

            queryObject.farm.fields.fieldsV2.fields.cropSeasons = {
                fields: {
                    uuid: {},
                    yieldExpectation: {},
                    preCropUuid: {},
                    startDate: {},
                    endDate: {},
                    isActive: {},
                    yield: {},
                    isHealthyField: {},
                    field: {
                        fields: {
                            uuid: {}
                        }
                    },
                    tillageUuid: {},
                    crop: {
                        fields: {
                            uuid: {}
                        }
                    },
                    variety: {
                        fields: {
                            uuid: {},
                            name: {}
                        }
                    },
                    cropUsage: {
                        fields: {
                            uuid: {}
                        }
                    }
                }
            };
        } catch (e) {}
    }

    static buildQuery (resourceList, ev) {

        let country = CURRENT_USER && CURRENT_USER.getCountry();

        if (!country || !ev.routeNamedArgs.languageCode) {
            return ;
        }

        let countryUuid = CountryUtils.getRegistrationCountryUuid(country);
        let languageCode  = CountryUtils.getLanguageCodeByUrlParam(ev.routeNamedArgs.languageCode).toUpperCase();
        let registrationStatusUuid = [VarietyCollection.REGISTRATION_STATUSES.REGISTERED.uuid];
        let queryObject = {};

        _.each(resourceList, (resource) => {

            switch (resource) {

                case ResourcesConfig.RESOURCE_NAME.CROPS:
                    GraphqlConfig.crops(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.STRESSES:
                    GraphqlConfig.stresses(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.VARIETIES:
                    GraphqlConfig.varieties(queryObject, languageCode, countryUuid, registrationStatusUuid);
                    break;
                case ResourcesConfig.RESOURCE_NAME.TILLAGE_SYSTEMS:
                    GraphqlConfig.tillageSystems(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.CROP_USAGES:
                    GraphqlConfig.cropUsages(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.SOIL_TEXTURES:
                    GraphqlConfig.soilTextures(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.SOIL_TAXONOMIES:
                    GraphqlConfig.soilTaxonomies(queryObject, languageCode);
                    break;
                case ResourcesConfig.RESOURCE_NAME.FARM:
                    GraphqlConfig.farm(queryObject, ev);
                    break;
                case ResourcesConfig.RESOURCE_NAME.FARM_FEATURES:
                    GraphqlConfig.farmFeatures(queryObject, ev);
                    break;
                case ResourcesConfig.RESOURCE_NAME.FIELDS:
                    GraphqlConfig.fields(queryObject, ev);
                    break;
                case ResourcesConfig.RESOURCE_NAME.CROP_SEASONS:
                    GraphqlConfig.cropSeasons(queryObject);
                    break;
                case ResourcesConfig.RESOURCE_NAME.USER: // User data is not fetched via graphql
                    break;
                default: console.warn(`Unsupported resource ${resource}`);
            }
        });

        return (!_.isEmpty(queryObject) ? graphqlify(queryObject) : undefined);
    }

    static fetchData (resourceList, ev) {

        let userData;
        let farmData;
        let fetchUser;
        let fetchFarm;
        let fetchOtherResources;
        let resources = [];
        let deferred = $.Deferred();
        let query = GraphqlConfig.buildQuery(resourceList, ev);

        if (_.includes(resourceList, ResourcesConfig.RESOURCE_NAME.USER)) {

            fetchUser = GraphqlConfig.user(ev)
                .done((user) => {
                    userData = user;
                })
                .fail(() => {
                    app.onLogout();
                });
        }

        if (_.includes(resourceList, ResourcesConfig.RESOURCE_NAME.FARM)) {

            fetchFarm = GraphqlConfig.fetchFarm(ev)
                .done((farm) => {
                    farmData = farm;
                })
                .fail(() => {
                    app.onLogout();
                });
        }

        /**
         * Fetching user details is included as part of default resources list,
         * if there is no session and we are loading any route that needs more data (ex: status page),
         * the page will be rendered without redirection and obviously the page will be empty.
         * Hence it is necessary to call the graphql service even if query is undefined so that
         * we get a 401 response that is handled in the app.js file at a global level
         */
        if (resourceList.length > 1) {

            let token = cookieReader(SECURITY_TOKEN_NAME);

            // If token exists and query is undefined, send default resources otherwise call the graphql API
            if (token && !query) {
                resources = GraphqlConfig.parseData(resourceList);
            } else {

                fetchOtherResources = GraphqlConfig.getGraphqlData(query)
                .done((response) => {
                    if (response.data && !response.errors) {
                        resources = GraphqlConfig.parseData(resourceList, response.data);
                    } else {

                        if (response.errors && response.errors[0] && response.errors[0].path && response.errors[0].path[0] === 'farm') {
                            // Show no toast
                        } else {
                            Radio.channel('NOTIFICATION').trigger('NOTIFY', {
                                content: __('SMTH_WRONG')
                            });
                        }

                        console.error(response.errors);

                        throw new Error('Graphql resource loader failed to load resources.');
                    }
                });
            }
        }

        $.when(fetchUser, fetchFarm, fetchOtherResources)
            .done(() => {

                if (_.includes(resourceList, ResourcesConfig.RESOURCE_NAME.USER)) {

                    let index = resourceList.indexOf(ResourcesConfig.RESOURCE_NAME.USER);

                    resources[index] = userData;
                }

                if (_.includes(resourceList, ResourcesConfig.RESOURCE_NAME.FARM)) {

                    let index = resourceList.indexOf(ResourcesConfig.RESOURCE_NAME.FARM);

                    resources[index] = farmData;
                }

                deferred.resolve(resources);
            });

        return deferred;
    }

    static buildCrops (data) {

        if (data && data.crops) {
            CROPS.set(data.crops, {parse: true});
        }

        return CROPS;
    }

    static buildStresses (data) {

        if (data && data.stressesV2) {

            STRESSES.set(data.stressesV2, {parse: true});
        }

        return STRESSES;
    }

    static buildVarieties (data) {

        if (data && data.varieties) {

            let varietyArray = [];

            _.each(data.varieties, (variety) => {

                let varietyObject = {
                    uuid: variety.uuid,
                    name: variety.name,
                    cropUuid: variety.crop.uuid,
                    breeder: variety.breeder
                };

                varietyArray.push(varietyObject);
            });

            return new VarietyCollection(varietyArray, {parse: true});
        }
    }

    static buildTillageSystems (data) {

        if (data && data.tillageSystems) {
            return new TillageSystemCollection(data.tillageSystems, {parse: true});
        }
    }

    static buildCropUsages (data) {

        if (data && data.cropUsages) {
            return new CropUsageCollection(data.cropUsages, {parse: true});
        }
    }

    static buildSoilTextures (data) {

        if (data && data.soilTextures) {
            return new SoilTextureCollection(data.soilTextures, {parse: true});
        }
    }

    static buildSoilTaxonomies (data) {

        if (data && data.soilTaxonomies) {
            return new SoilTaxonomyCollection(data.soilTaxonomies, {parse: true});
        }
    }

    static buildFarm (data) {

        if (data && data.farm) {

            let farmObject = {
                uuid: data.farm.uuid,
                name: data.farm.name,
                creatorUuid: data.farm.creatorUuid,
                access: data.farm.access,
                timezone: data.farm.timezone,
                latitude: data.farm.latitude,
                longitude: data.farm.longitude,
            };

            CURRENT_FARM.set(farmObject);

            if (BaseModel.timezone !== CURRENT_FARM.get('timezone')) {
                CURRENT_FARM.updateBaseTimezone();
            }
        }

        return CURRENT_FARM;
    }

    static buildFarmFeatures (data) {

        let farmFeatures = CURRENT_FARM_FEATURES;

        if (data && data.farm && data.farm.features) {

            farmFeatures.reset();

            let featureArray = [];

            _.each(data.farm.features, (feature) => {

                let featureObject = {
                    farmUuid: feature.farmUuid,
                    cropUuid: (feature.crop ? feature.crop.uuid : undefined),
                    name: feature.name,
                    validTill: feature.validTill,
                    packageUuid: feature.packageUuid,
                    packageType: feature.packageType
                };

                featureArray.push(featureObject);
            });

            farmFeatures.set(featureArray, {parse: true});
        }

        return farmFeatures;
    }

    static buildFields (data) {

        if (data &&
            data.farm &&
            data.farm.fieldsV2) {

            CURRENT_FIELDS.reset();

            let fieldArray = [];

            _.each(data.farm.fieldsV2, (field) => {

                let fieldObject = {
                    uuid: field.uuid,
                    name: field.name,
                    area: field.area,
                    heading: field.heading,
                    geometry: field.boundary,
                    boundarySvg: field.boundarySvg,
                    farmUuid: field.farm.uuid,
                    soilTextureUuid: (field.soilTexture ? field.soilTexture.uuid : undefined),
                    soilTaxonomyUuid: (field.soilTaxonomy ? field.soilTaxonomy.uuid : undefined),
                    features: field.features
                };

                fieldArray.push(fieldObject);
            });

            CURRENT_FIELDS.set(fieldArray, {parse: true});
        }

        return CURRENT_FIELDS;
    }

    static buildCropSeasons (data) {

        if (data && data.farm && data.farm.fieldsV2) {

            CURRENT_CROP_SEASONS.reset();

            let csArray = [];

            _.each(data.farm.fieldsV2, (field) => {

                if (field.cropSeasons && field.cropSeasons.length) {

                    csArray = _.concat(csArray, field.cropSeasons);
                }
            });

            let csArrayFiltered = [];

            _.each(csArray, (cs) =>{

                let csObject = {
                    uuid: cs.uuid,
                    yield: cs.yield,
                    yieldExpectation: cs.yieldExpectation,
                    preCropUuid: cs.preCropUuid,
                    startDate: cs.startDate,
                    endDate: cs.endDate,
                    isActive: cs.isActive,
                    fieldUuid: cs.field.uuid,
                    tillageUuid: cs.tillageUuid,
                    cropUuid: cs.crop.uuid,
                    varietyUuid: (cs.variety ? cs.variety.uuid : undefined),
                    varietyName: (cs.variety ? cs.variety.name : undefined),
                    isHealthyField: cs.isHealthyField,
                    cropUsageUuid: (cs.cropUsage ? cs.cropUsage.uuid : undefined)
                };

                csArrayFiltered.push(csObject);
            });

            CURRENT_CROP_SEASONS.set(csArrayFiltered, {parse: true});
        }

        return CURRENT_CROP_SEASONS;
    }

    static parseData (resourceList, data) {

        return _.map(resourceList, (resource) => {

            switch (resource) {

                case ResourcesConfig.RESOURCE_NAME.CROPS:
                    return GraphqlConfig.buildCrops(data);
                case ResourcesConfig.RESOURCE_NAME.STRESSES:
                    return GraphqlConfig.buildStresses(data);
                case ResourcesConfig.RESOURCE_NAME.VARIETIES:
                    return GraphqlConfig.buildVarieties(data);
                case ResourcesConfig.RESOURCE_NAME.TILLAGE_SYSTEMS:
                    return GraphqlConfig.buildTillageSystems(data);
                case ResourcesConfig.RESOURCE_NAME.CROP_USAGES:
                    return GraphqlConfig.buildCropUsages(data);
                case ResourcesConfig.RESOURCE_NAME.SOIL_TEXTURES:
                    return GraphqlConfig.buildSoilTextures(data);
                case ResourcesConfig.RESOURCE_NAME.SOIL_TAXONOMIES:
                    return GraphqlConfig.buildSoilTaxonomies(data);
                case ResourcesConfig.RESOURCE_NAME.FARM:
                    return GraphqlConfig.buildFarm(data);
                case ResourcesConfig.RESOURCE_NAME.FARM_FEATURES:
                    return GraphqlConfig.buildFarmFeatures(data);
                case ResourcesConfig.RESOURCE_NAME.FIELDS:
                    return GraphqlConfig.buildFields(data);
                case ResourcesConfig.RESOURCE_NAME.CROP_SEASONS:
                    return GraphqlConfig.buildCropSeasons(data);
                case ResourcesConfig.RESOURCE_NAME.USER:
                    return null; // User data is not fetched via graphql, Soil Texture, Soil Taxonomy to be fetched later
                default: throw new Error(`Unsupported resource ${resource}`);
            }
        });
    }

    static getGraphqlData(query) {

        if(ENV === 'demo') {
            let deferred = new $.Deferred();

            require.ensure(['./DemoData'], (require) => {
                let demoData = require('./DemoData').DemoData;

                deferred.resolve(demoData.getResourcesData());
            });

            return deferred;

        } else {
            return $.ajax({
                url: `${API_PREFIX}/graphql/data?query=${query}`,
                type: 'GET',
                dataType: 'json',
            });
        }
    }
}


// WEBPACK FOOTER //
// ./js/resources/GraphqlConfig.js